// AdminProduct (adminTypes.ts) is sourced from a CSV catalog import with no
// schema validation at the ingestion boundary — see the "Admin wire types"
// note in AGENTS.md. A wrong-typed or unparseable CSV cell for any field
// currently becomes a silent `undefined`/NaN once it passes through an
// unchecked field access, rather than a loud, traceable failure.
//
// AdminProduct itself stays open/loose on purpose (per AGENTS.md, closing
// it globally is the wrong fix for a CSV-sourced catalog). Instead, each
// schema in this file validates only the fields one real call site
// actually reads — deliberately not a schema for the whole open
// AdminProduct shape, per AGENTS.md's "don't over-engineer" guidance.
// `adminProductBomFieldsSchema` is the validation boundary for
// `packages/room-flow/src/diyBom.ts` (arithmetic: price x qty, coverage_m2
// division; URL building). `adminProductCatalogFieldsSchema` is the same
// pattern for `packages/room-flow/src/panelCatalog.ts` (catalog-grid
// filter/sort + panel-from-product placement math). Add a new narrow
// schema here, following the same shape, for any other call site that
// starts trusting raw AdminProduct fields for arithmetic or identity.
//
// A field that's genuinely absent from the CSV row (undefined, null, or
// "") is fine — each call site already has documented fallbacks for that.
// What must be loud is a field that's *present* but the wrong shape: a
// boolean where a number belongs, a non-numeric string in a price cell,
// etc. — that's the case that used to silently degrade to `undefined`/NaN
// deep in downstream computation or rendering.

import { z } from 'zod';
import type { AdminProduct } from './adminTypes';

function blankToUndefined(v: unknown): unknown {
  return v === null || v === '' ? undefined : v;
}

/** Accepts a JS number or a numeric CSV string ("1999" -> 1999). Anything
 *  else that isn't blank (a boolean, an object, a non-numeric string like
 *  "twelve dollars") fails validation instead of quietly becoming null. */
const numericCsvField = z.preprocess(
  blankToUndefined,
  z
    .union([
      z.number(),
      z
        .string()
        .refine((s) => s.trim().length > 0 && Number.isFinite(Number(s)), {
          error: 'must be a number or a numeric string',
        })
        .transform(Number),
    ])
    .optional(),
);

/** Any non-blank string. Blank ("") and null normalize to "absent" —
 *  matching the old stringField helper's behavior of treating an empty
 *  cell the same as a missing one. */
const stringCsvField = z.preprocess(blankToUndefined, z.string().optional());

/** Shared by every `parseAdminProduct*Fields` function below: builds the
 *  same "Malformed catalog product "<sku>": <field> — <message>; ..." error
 *  shape so every validation boundary in this file reads identically,
 *  regardless of which narrow field set it's checking. */
function malformedProductError(product: AdminProduct, error: z.ZodError): Error {
  const skuLabel =
    typeof product?.sku === 'string' && product.sku.length > 0 ? product.sku : '<unknown sku>';
  const issues = error.issues
    .map((issue) => `${issue.path.join('.') || '(root)'} — ${issue.message}`)
    .join('; ');
  return new Error(`Malformed catalog product "${skuLabel}": ${issues}`);
}

/** The subset of `AdminProduct` that `packages/room-flow/src/diyBom.ts`
 *  reads: `sku`/`name` for the BOM line identity, `price_cents` for cost,
 *  `coverage_m2` for pack-quantity math, `unit` for the line's unit label,
 *  and `asin`/`search_terms` for the purchase URL. */
export const adminProductBomFieldsSchema = z.object({
  sku: z.string().min(1, 'sku is required and must be a non-empty string'),
  name: stringCsvField,
  price_cents: numericCsvField,
  coverage_m2: numericCsvField,
  unit: stringCsvField,
  asin: stringCsvField,
  search_terms: stringCsvField,
});

export type AdminProductBomFields = z.infer<typeof adminProductBomFieldsSchema>;

/** Validates the BOM-relevant fields of a catalog product. Throws a
 *  descriptive `Error` naming the SKU and the failing field(s) on
 *  malformed data — matching `packages/room-repository`'s convention of
 *  throwing a plain, descriptive `Error` rather than returning a Result
 *  type or swallowing the failure into a console warning. */
export function parseAdminProductBomFields(product: AdminProduct): AdminProductBomFields {
  const result = adminProductBomFieldsSchema.safeParse(product);
  if (result.success) return result.data;
  throw malformedProductError(product, result.error);
}

/** The subset of `AdminProduct` that `packages/room-flow/src/panelCatalog.ts`
 *  reads: `sku`/`name`/`category` for catalog-grid identity, `active`/
 *  `image_url`/`price_cents` for filtering and sorting the visible catalog
 *  list, and `mounting_type`/`width_m`/`height_m`/`depth_m` (or their `_cm`
 *  counterparts) for turning a clicked catalog card into a placed panel's
 *  size and mount. Same narrow-schema-at-point-of-use pattern as
 *  `adminProductBomFieldsSchema` above — see that schema's comment and
 *  AGENTS.md's "Admin wire types" note for why this isn't a whole-shape
 *  `AdminProduct` schema. */
export const adminProductCatalogFieldsSchema = z.object({
  sku: z.string().min(1, 'sku is required and must be a non-empty string'),
  name: stringCsvField,
  category: stringCsvField,
  active: numericCsvField,
  image_url: stringCsvField,
  price_cents: numericCsvField,
  mounting_type: stringCsvField,
  width_m: numericCsvField,
  height_m: numericCsvField,
  depth_m: numericCsvField,
  width_cm: numericCsvField,
  height_cm: numericCsvField,
  depth_cm: numericCsvField,
});

export type AdminProductCatalogFields = z.infer<typeof adminProductCatalogFieldsSchema>;

/** Validates the catalog-grid/placement-relevant fields of a catalog
 *  product. Same throw-loudly-on-malformed, fall-through-on-absent
 *  contract as `parseAdminProductBomFields` — see that function's comment. */
export function parseAdminProductCatalogFields(product: AdminProduct): AdminProductCatalogFields {
  const result = adminProductCatalogFieldsSchema.safeParse(product);
  if (result.success) return result.data;
  throw malformedProductError(product, result.error);
}
